import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { FileText, Download, Search, Filter, ChevronUp, ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import api from '../services/api'
import type { Invoice, PaginationMeta } from '../types'
import {
  formatCurrency,
  formatDate,
  getInvoiceStatusLabel,
  getInvoiceStatusColor,
} from '../utils/helpers'
import Pagination from '../components/Pagination'
import LinePreviewPopup from '../components/LinePreviewPopup'
import InvoicePayButton from '../components/InvoicePayButton'

export default function Invoices() {
  const { t } = useTranslation()
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [filteredInvoices, setFilteredInvoices] = useState<Invoice[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [pagination, setPagination] = useState<PaginationMeta | null>(null)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [sortField, setSortField] = useState<string | null>('date')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [error, setError] = useState<string | null>(null)
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  const [hoveredRect, setHoveredRect] = useState<DOMRect | null>(null)
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const loadInvoices = useCallback(async () => {
    setLoading(true)
    try {
      const response = await api.getInvoices({ page, limit })
      const list = Array.isArray(response) ? response : (response?.data ?? [])
      const pag = Array.isArray(response) ? null : (response?.pagination ?? null)
      setInvoices(list)
      setFilteredInvoices(list)
      setPagination(pag)
      setError(null)
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: string } } }
      console.error('Erreur chargement factures:', err)
      setError(apiErr?.response?.data?.error || t('invoices.loadError'))
    } finally {
      setLoading(false)
    }
  }, [page, limit, t])

  const filterInvoices = useCallback(() => {
    let filtered = invoices

    if (searchTerm) {
      filtered = filtered.filter(
        (inv) =>
          inv.ref.toLowerCase().includes(searchTerm.toLowerCase()) ||
          inv.customer_ref?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    if (statusFilter !== 'all') {
      if (statusFilter === 'paid') {
        filtered = filtered.filter((inv) => inv.is_paid === 1 || inv.is_paid === true)
      } else if (statusFilter === 'unpaid') {
        filtered = filtered.filter(
          (inv) =>
            inv.is_paid !== 1 &&
            inv.is_paid !== true &&
            (inv.status === 1 || inv.status === 'validated')
        )
      } else if (statusFilter === 'draft') {
        filtered = filtered.filter((inv) => inv.status === 0 || inv.status === 'draft')
      }
    }

    if (sortField) {
      filtered = [...filtered].sort((a, b) => {
        const va = (a as unknown as Record<string, unknown>)[sortField] ?? ''
        const vb = (b as unknown as Record<string, unknown>)[sortField] ?? ''
        if (typeof va === 'number' && typeof vb === 'number')
          return sortDir === 'asc' ? va - vb : vb - va
        return sortDir === 'asc'
          ? String(va).localeCompare(String(vb))
          : String(vb).localeCompare(String(va))
      })
    }

    setFilteredInvoices(filtered)
  }, [invoices, searchTerm, statusFilter, sortField, sortDir])

  useEffect(() => {
    loadInvoices()
  }, [loadInvoices])

  useEffect(() => {
    filterInvoices()
  }, [filterInvoices])

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortDir('desc')
    }
  }

  const handleRowMouseEnter = (e: React.MouseEvent<HTMLTableRowElement>, id: string) => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current)
    const target = e.currentTarget // capture before setTimeout (React nullifies currentTarget after handler)
    hoverTimer.current = setTimeout(() => {
      setHoveredId(id)
      setHoveredRect(target.getBoundingClientRect())
    }, 400)
  }

  const handleRowMouseLeave = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current)
    setHoveredId(null)
    setHoveredRect(null)
  }

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return <ChevronUp className="w-3 h-3 text-gray-300 inline ml-1" />
    return sortDir === 'asc' ? (
      <ChevronUp className="w-3 h-3 text-primary-600 inline ml-1" />
    ) : (
      <ChevronDown className="w-3 h-3 text-primary-600 inline ml-1" />
    )
  }

  const handleDownloadPDF = async (invoice: Invoice) => {
    try {
      const pdfData = await api.downloadInvoicePDF(invoice.id, invoice._erp_id)
      if (!pdfData) {
        alert(t('invoices.noPdfData', 'Aucune donnée PDF reçue du serveur'))
        return
      }
      const byteCharacters = atob(pdfData)
      const byteArray = new Uint8Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) byteArray[i] = byteCharacters.charCodeAt(i)
      const blob = new Blob([byteArray], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${invoice.ref}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch {
      alert(t('invoices.downloadError', 'Erreur lors du téléchargement du PDF'))
    }
  }

  const handlePageChange = (p: number) => {
    setPage(p)
  }
  const handleLimitChange = (l: number) => {
    setLimit(l)
    setPage(1)
  }

  const totalAmount = filteredInvoices.reduce((sum, inv) => sum + inv.total_incl_tax, 0)
  const unpaidAmount = filteredInvoices
    .filter((inv) => inv.is_paid !== 1 && inv.is_paid !== true)
    .reduce((sum, inv) => sum + inv.remaining_amount, 0)

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-800 font-medium mb-2">{error}</p>
          <button
            onClick={loadInvoices}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            {t('common.retry')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header with stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <p className="text-sm font-medium text-gray-600">{t('invoices.totalInvoices')}</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{filteredInvoices.length}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <p className="text-sm font-medium text-gray-600">{t('invoices.totalAmount')}</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{formatCurrency(totalAmount)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <p className="text-sm font-medium text-gray-600">{t('invoices.outstanding')}</p>
          <p className="text-3xl font-bold text-red-600 mt-2">{formatCurrency(unpaidAmount)}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={t('invoices.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none appearance-none bg-white"
            >
              <option value="all">{t('invoices.filterAll')}</option>
              <option value="paid">{t('invoices.filterPaid')}</option>
              <option value="unpaid">{t('invoices.filterUnpaid')}</option>
              <option value="draft">{t('invoices.filterDraft')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Invoices list */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {filteredInvoices.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p>{t('invoices.notFound')}</p>
          </div>
        ) : (
          <>
            {/* Mobile card layout */}
            <ul className="divide-y divide-gray-100 sm:hidden">
              {filteredInvoices.map((invoice) => (
                <li key={invoice.id} className="p-4 space-y-1.5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      {pagination && (
                        <p className="text-xs text-gray-500 truncate">
                          {invoice.customer_name ?? '—'}
                        </p>
                      )}
                      <Link
                        to={`/invoices/${invoice.id}${invoice._erp_id ? `?erp_id=${invoice._erp_id}` : ''}`}
                        className="font-medium text-primary-600 hover:text-primary-700 text-sm"
                      >
                        {invoice.ref}
                      </Link>
                    </div>
                    <span
                      className={`flex-shrink-0 inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${getInvoiceStatusColor(invoice.status, invoice.is_paid)}`}
                    >
                      {getInvoiceStatusLabel(invoice.status, invoice.is_paid, t)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2 text-xs text-gray-500">
                    <span>{formatDate(invoice.date)}</span>
                    <span className="font-semibold text-sm text-gray-900">
                      {formatCurrency(invoice.total_incl_tax)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-gray-400">
                      {t('invoices.colDue')} : {formatDate(invoice.due_date)}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => handleDownloadPDF(invoice)}
                        className="text-gray-500 hover:text-primary-600 transition-colors"
                        title={t('common.download')}
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      {!invoice.is_paid && <InvoicePayButton invoice={invoice} />}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            {/* Desktop table layout */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {pagination && (
                      <th
                        className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('customer_name')}
                      >
                        {t('common.client')} <SortIcon field="customer_name" />
                      </th>
                    )}
                    <th
                      className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('ref')}
                    >
                      {t('invoices.colRef')} <SortIcon field="ref" />
                    </th>
                    <th
                      className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('date')}
                    >
                      {t('invoices.colDate')} <SortIcon field="date" />
                    </th>
                    <th
                      className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('due_date')}
                    >
                      {t('invoices.colDue')} <SortIcon field="due_date" />
                    </th>
                    <th
                      className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('total_incl_tax')}
                    >
                      {t('invoices.colAmount')} <SortIcon field="total_incl_tax" />
                    </th>
                    <th
                      className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('status')}
                    >
                      {t('invoices.colStatus')} <SortIcon field="status" />
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('invoices.colActions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredInvoices.map((invoice) => (
                    <tr
                      key={invoice.id}
                      className="hover:bg-gray-50 transition-colors"
                      onMouseEnter={(e) => handleRowMouseEnter(e, invoice.id)}
                      onMouseLeave={handleRowMouseLeave}
                    >
                      {pagination && (
                        <td className="px-6 py-4 text-sm text-gray-700 font-medium whitespace-nowrap">
                          {invoice.customer_name ?? '—'}
                        </td>
                      )}
                      <td className="px-6 py-4">
                        <Link
                          to={`/invoices/${invoice.id}${invoice._erp_id ? `?erp_id=${invoice._erp_id}` : ''}`}
                          className="font-medium text-primary-600 hover:text-primary-700"
                        >
                          {invoice.ref}
                        </Link>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {formatDate(invoice.date)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {formatDate(invoice.due_date)}
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                        {formatCurrency(invoice.total_incl_tax)}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${getInvoiceStatusColor(invoice.status, invoice.is_paid)}`}
                        >
                          {getInvoiceStatusLabel(invoice.status, invoice.is_paid, t)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleDownloadPDF(invoice)}
                            className="text-gray-600 hover:text-primary-600 transition-colors"
                            title={t('common.download')}
                          >
                            <Download className="w-5 h-5" />
                          </button>
                          {!invoice.is_paid && <InvoicePayButton invoice={invoice} />}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        {pagination && (
          <div className="px-6 py-4">
            <Pagination
              pagination={pagination}
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
            />
          </div>
        )}
      </div>

      {/* Hover line preview */}
      {hoveredId && hoveredRect && (
        <LinePreviewPopup id={hoveredId} type="invoice" anchorRect={hoveredRect} />
      )}
    </div>
  )
}
